// src/screens/Home/Home.tsx
import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import styles from './Home.module.css'
import PageContainer from '../../components/PageElements/PageContainer/PageContainer'
import PageBody from '../../components/PageElements/PageBody/PageBody'
import Header from '../../components/Header'
import PortalHeader from '../../components/PortalHeader/PortalHeader'
import DigitalIDCard from '../../components/DigitalIDCard/DigitalIDCard'
import PlatformStats from '../../components/PlatformStats/PlatformStats'
import { usePageSEO } from '../../hooks/usePageSEO'
import { getMatchTooltip, getConflictTooltip } from '../../utils/dietaryTooltips'
import chickpeasaladImage from '../../images/chickpeasalad.png'
import pumpkinSoupImage from '../../images/pumpkin_soup_8.jpg'
import baconImage from '../../images/bacon.png'
import pizzaImage from '../../images/pizza.png'
import avocadoImage from '../../images/avocado.png'
import mquinoabowlImage from '../../images/mquinoabowl.png'
import quinoaImage from '../../images/quinoa.png'
import smoothieImage from '../../images/smoothie.png'
import porkImage from '../../images/pork.png'
import chartsWidgetsImage from '../../images/ChartsWidgets.png'
import dataExplorerImage from '../../images/DataExplorer.png'

const Home: React.FC = () => {
  return <HomeContent />
}

const HomeContent: React.FC = () => {
  const location = useLocation();
  const isPortalLanding = location.pathname === '/portal/landing';
  
  // SEO hook for the home page
  usePageSEO({
    title: isPortalLanding 
      ? "Business Portal | PlatefullAI - Track Customer Dietary Preferences"
      : "Your Digital Dietary Identity | PlatefullAI - Carry Your Diet Everywhere",
    description: isPortalLanding
      ? "Get detailed insights into your customers' dietary preferences and health concerns. Track visitor data and adapt your menu accordingly."
      : "Create your digital dietary identity and carry your dietary preferences everywhere you eat. Seamlessly connect with restaurants, recipe sites, and food services.",
    keywords: isPortalLanding
      ? "business analytics, customer insights, dietary preferences, restaurant management, visitor tracking, menu optimization"
      : "digital dietary identity, dietary preferences, personalized nutrition, food allergies, dietary restrictions, restaurant integration, recipe personalization, meal planning, food technology",
    canonicalUrl: isPortalLanding ? "https://platefullai.com/portal/landing" : "https://platefullai.com"
  });

  const { user, logout, showLoginForm } = useAuth()
  const [activePlatform, setActivePlatform] = useState('delivery');
  const [recipesLoading] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState(0);

  const handleLogin = () => {
    showLoginForm('login');
  };

  // Dashboard carousel data
  const dashboards = [
    {
      id: 'charts',
      title: 'Charts & Widgets',
      subtitle: 'Dietary Preferences - Madeira',
      image: chartsWidgetsImage,
      alt: 'Charts & Widgets Dashboard showing dietary preferences analytics'
    },
    {
      id: 'explorer',
      title: 'Data Explorer',
      subtitle: 'Explore detailed visit data and user preferences for your venues.',
      image: dataExplorerImage,
      alt: 'Data Explorer showing visitor information and dietary preferences'
    }
  ];


  // Auto-switch dashboards every 4 seconds
  useEffect(() => {
    if (!isPortalLanding) return;
    
    const interval = setInterval(() => {
      setActiveDashboard((prev) => (prev + 1) % dashboards.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPortalLanding, dashboards.length]);

  const goToDashboard = (index: number) => {
    setActiveDashboard(index);
  };

  return (
    <PageContainer>
      {isPortalLanding ? (
        <PortalHeader 
          showContentHeader={false}
        />
      ) : (
        <Header user={user} onLogin={handleLogin} onLogout={logout} />
      )}

      <PageBody>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroText}>
              <h1 className={styles.heroTitle}>{isPortalLanding ? 'Customize the customer experience' : 'Your Digital Dietary Identity'}</h1>
              {isPortalLanding ? (
                <>
                  <p className={styles.heroDescription}>
                    PlatefullAI lets your customers view the menu through their own dietary lens. There is more than
                    vegan, vegetarian, and gluten‑free. Many guests need higher iron, want to manage blood pressure,
                    or are treating diabetes and other specific needs. You cannot place twenty indicators on a menu,
                    so we label and filter for each guest. You also see customers’ dietary preferences, not just what
                    they order, so you can serve them better.
                  </p>
                </>
              ) : user ? (
                <>
                  <p className={styles.heroSubtitle}>
                    How to use your digital dietary ID
                  </p>
                  <div className={styles.heroDescription}>
                    <p><strong>Tap in at restaurants</strong> - Scan QR codes or show your ID card to automatically share your dietary preferences and get personalized menu recommendations.</p>
                    <p><strong>Checkout any food website</strong> - You'll notice your preferences automatically applied on integrated platforms without having to do anything.</p>
                    <p><strong>Heading to a party?</strong> - Share your ID with hosts so they know exactly what you can eat and what to avoid.</p>
                    <p><strong>Hosting guests?</strong> - Ask them to share their dietary IDs so you can plan meals that work for everyone.</p>
                  </div>
                </>
              ) : (
                <>
                  <p className={styles.heroSubtitle}>
                    Carry your dietary preferences everywhere you eat
                  </p>
                  <p className={styles.heroDescription}>
                    Sign up with the email you use for most services (DoorDash, Instacart, etc.), and your digital dietary ID will be 
                    automatically attached to your online presence across all integrated platforms. Create your 
                    personalized dietary profile ONCE, and connect with restaurants, recipe sites, 
                    grocery stores, and meal services. The goal is to make it easy to avoid allergens 
                    and find the right food for you.
                  </p>
                </>
              )}
            </div>
            <div className={styles.heroCard}>
              {isPortalLanding ? (
                <DigitalIDCard
                  mockUser={{
                    email: 'demo@platefull.ai',
                    id: 'demo-user-1234',
                    preferences: {
                      dietary: ['vegetarian', 'gluten_free'],
                      ingredientPreferences: {
                        likes: ['chickpeas', 'quinoa', 'spinach'],
                        avoids: ['shellfish', 'peanuts']
                      },
                      healthConcerns: ['high_blood_pressure', 'diabetes']
                    }
                  }}
                  showActions={false}
                />
              ) : (
                <DigitalIDCard />
              )}
            </div>
          </div>
        </div>

        {/* Platform Integration Slideshow */}
        <div className={styles.integrationSection}>
          <h2 className={styles.sectionTitle}>See It In Action</h2>
          <p className={styles.sectionDescription}>
            Your digital dietary ID works across all food platforms:
          </p>
          
          {/* Demo Website Section */}
          <div className={styles.demoSection}>
            <div className={styles.demoCard}>
              <h3 className={styles.demoTitle}>Visit Our Demo Website</h3>
              <p className={styles.demoDescription}>
                See PlatefullAI in action at our partner restaurant demo site. 
                Experience personalized menu recommendations and ingredient insights in real-time.
              </p>
              <a 
                href="https://madeira-5a33c40d3637.herokuapp.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.demoButton}
              >
                Visit Madeira Restaurant Demo
                <span className={styles.externalLinkIcon}>↗</span>
              </a>
            </div>
          </div>
          
          <div className={styles.slideshowContainer}>
            <div className={styles.platformTabs}>
              <button 
                className={`${styles.platformTab} ${activePlatform === 'delivery' ? styles.active : ''}`}
                onClick={() => setActivePlatform('delivery')}
              >
                Delivery Apps
              </button>
              <button 
                className={`${styles.platformTab} ${activePlatform === 'grocery' ? styles.active : ''}`}
                onClick={() => setActivePlatform('grocery')}
              >
                Grocery Stores
              </button>
              <button 
                className={`${styles.platformTab} ${activePlatform === 'recipes' ? styles.active : ''}`}
                onClick={() => setActivePlatform('recipes')}
              >
                Recipe Sites
              </button>
            </div>

            <div className={styles.slideContent}>
              {activePlatform === 'delivery' && (
                <div className={styles.comparisonContainer}>
                  <div className={styles.comparisonSide}>
                    <div className={styles.comparisonLabel}>Without PlatefullAI</div>
                    <div className={styles.platformSlide}>
                      <div className={`${styles.platformHeader} ${styles.deliveryHeader}`}>
                        <div className={styles.platformLogo}>Food Delivery</div>
                      </div>
                      
                      <div className={styles.restaurantHeader}>
                        <h3 className={styles.restaurantName}>Green Garden Bistro</h3>
                        <div className={styles.noMatch}>
                          <span className={styles.matchText}>All items shown</span>
                        </div>
                      </div>

                      <div className={styles.menuItems}>
                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={chickpeasaladImage} alt="Quinoa Buddha Bowl" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Quinoa Buddha Bowl</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              Quinoa, avocado, chickpeas, coconut oil dressing
                            </p>
                            <div className={styles.itemPrice}>$12.99</div>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={pumpkinSoupImage} alt="Coconut Curry Soup" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Coconut Curry Soup</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              Coconut milk, vegetables, quinoa
                            </p>
                            <div className={styles.itemPrice}>$11.99</div>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={pizzaImage} alt="Pepperoni Pizza" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Pepperoni Pizza</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              Pepperoni, dairy cheese, gluten crust
                            </p>
                            <div className={styles.itemPrice}>$14.99</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.comparisonSide}>
                    <div className={styles.comparisonLabel}>With PlatefullAI</div>
                    <div className={styles.platformSlide}>
                      <div className={`${styles.platformHeader} ${styles.deliveryHeader}`}>
                        <div className={styles.platformLogo}>Food Delivery</div>
                      </div>
                      
                      <div className={styles.restaurantHeader}>
                        <h3 className={styles.restaurantName}>Green Garden Bistro</h3>
                        <div className={styles.dietaryMatch}>
                          <span className={styles.matchText}>Dietary ID Active</span>
                        </div>
                      </div>

                      <div className={styles.menuItems}>
                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={chickpeasaladImage} alt="Quinoa Buddha Bowl" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Quinoa Buddha Bowl</h4>
                              <div className={styles.dietaryTags}>
                                <span className={`${styles.dietaryTag} ${styles.vegan}`}>Vegan</span>
                                <span className={`${styles.dietaryTag} ${styles.glutenFree}`}>Gluten-Free</span>
                              </div>
                            </div>
                            <p className={styles.itemDescription}>
                              Quinoa, <span className={`${styles.highlightedIngredient} ${styles.like}`} title={getMatchTooltip('vegan', 'avocado')}>avocado</span>, 
                              <span className={`${styles.highlightedIngredient} ${styles.like}`} title={getMatchTooltip('vegan', 'chickpeas')}>chickpeas</span>, 
                              <span className={`${styles.highlightedIngredient} ${styles.like}`} title={getMatchTooltip('vegan', 'coconut oil')}>coconut oil</span> dressing
                            </p>
                            <div className={styles.itemPrice}>$12.99</div>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={pumpkinSoupImage} alt="Coconut Curry Soup" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Coconut Curry Soup</h4>
                              <div className={styles.dietaryTags}>
                                <span className={`${styles.dietaryTag} ${styles.vegan}`}>Vegan</span>
                                <span className={`${styles.dietaryTag} ${styles.nutFree}`}>Nut-Free</span>
                              </div>
                            </div>
                            <p className={styles.itemDescription}>
                              <span className={`${styles.highlightedIngredient} ${styles.like}`}>Coconut</span> milk, 
                              vegetables, <span className={`${styles.highlightedIngredient} ${styles.like}`}>quinoa</span>
                            </p>
                            <div className={styles.itemPrice}>$11.99</div>
                          </div>
                        </div>

                        <div className={`${styles.menuItem} ${styles.eliminated}`}>
                          <div className={styles.itemImage}>
                            <img src={pizzaImage} alt="Pepperoni Pizza" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Pepperoni Pizza</h4>
                              <div className={styles.dietaryTags}>
                                <span className={`${styles.dietaryTag} ${styles.eliminated}`}>Contains Pork</span>
                              </div>
                            </div>
                            <p className={styles.itemDescription}>
                              <span className={`${styles.highlightedIngredient} ${styles.avoid}`} title={getConflictTooltip('vegan', 'Pepperoni')}>Pepperoni</span>, 
                              <span className={`${styles.highlightedIngredient} ${styles.avoid}`} title={getConflictTooltip('dairy_free', 'dairy cheese')}>dairy</span> cheese, 
                              <span className={`${styles.highlightedIngredient} ${styles.avoid}`} title={getConflictTooltip('gluten_free', 'gluten crust')}>gluten</span> crust
                            </p>
                            <div className={styles.itemPrice}>$14.99</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePlatform === 'grocery' && (
                <div className={styles.comparisonContainer}>
                  <div className={styles.comparisonSide}>
                    <div className={styles.comparisonLabel}>Without PlatefullAI</div>
                    <div className={styles.platformSlide}>
                      <div className={`${styles.platformHeader} ${styles.groceryHeader}`}>
                        <div className={styles.platformLogo}>Grocery</div>
                      </div>
                      
                      <div className={styles.restaurantHeader}>
                        <h3 className={styles.restaurantName}>Fresh Produce & Proteins</h3>
                        <div className={styles.noMatch}>
                          <span className={styles.matchText}>All items shown</span>
                        </div>
                      </div>

                      <div className={styles.menuItems}>
                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={avocadoImage} alt="Organic Avocados" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Organic Avocados</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              Fresh avocado
                            </p>
                            <div className={styles.itemPrice}>$2.99 each</div>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={quinoaImage} alt="Quinoa Grain" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Quinoa Grain</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              Organic quinoa
                            </p>
                            <div className={styles.itemPrice}>$8.99</div>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={baconImage} alt="Bacon" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Bacon</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              Thick-cut bacon
                            </p>
                            <div className={styles.itemPrice}>$8.99/lb</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.comparisonSide}>
                    <div className={styles.comparisonLabel}>With PlatefullAI</div>
                    <div className={styles.platformSlide}>
                      <div className={`${styles.platformHeader} ${styles.groceryHeader}`}>
                        <div className={styles.platformLogo}>Grocery</div>
                      </div>
                      
                      <div className={styles.restaurantHeader}>
                        <h3 className={styles.restaurantName}>Fresh Produce & Proteins</h3>
                        <div className={styles.dietaryMatch}>
                          <span className={styles.matchText}>Dietary ID Active</span>
                        </div>
                      </div>

                      <div className={styles.menuItems}>
                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={avocadoImage} alt="Organic Avocados" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Organic Avocados</h4>
                              <div className={styles.dietaryTags}>
                                <span className={`${styles.dietaryTag} ${styles.liked}`}>You Love</span>
                              </div>
                            </div>
                            <p className={styles.itemDescription}>
                              Fresh <span className={`${styles.highlightedIngredient} ${styles.like}`} title={getMatchTooltip('vegan', 'avocado')}>avocado</span> - perfect for your diet
                            </p>
                            <div className={styles.itemPrice}>$2.99 each</div>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={quinoaImage} alt="Quinoa Grain" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Quinoa Grain</h4>
                              <div className={styles.dietaryTags}>
                                <span className={`${styles.dietaryTag} ${styles.liked}`}>You Love</span>
                              </div>
                            </div>
                            <p className={styles.itemDescription}>
                              Organic <span className={`${styles.highlightedIngredient} ${styles.like}`}>quinoa</span> - gluten-free protein
                            </p>
                            <div className={styles.itemPrice}>$8.99</div>
                          </div>
                        </div>

                        <div className={`${styles.menuItem} ${styles.eliminated}`}>
                          <div className={styles.itemImage}>
                            <img src={baconImage} alt="Bacon" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Bacon</h4>
                              <div className={styles.dietaryTags}>
                                <span className={`${styles.dietaryTag} ${styles.eliminated}`}>Contains Pork</span>
                              </div>
                            </div>
                            <p className={styles.itemDescription}>
                              Thick-cut <span className={`${styles.highlightedIngredient} ${styles.avoid}`} title={getConflictTooltip('vegan', 'bacon')}>bacon</span> - hidden from your view
                            </p>
                            <div className={styles.itemPrice}>$8.99/lb</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePlatform === 'recipes' && (
                <div className={styles.comparisonContainer}>
                  <div className={styles.comparisonSide}>
                    <div className={styles.comparisonLabel}>Without PlatefullAI</div>
                    <div className={styles.platformSlide}>
                      <div className={`${styles.platformHeader} ${styles.recipesHeader}`}>
                        <div className={styles.platformLogo}>Recipe Websites</div>
                      </div>
                      
                      <div className={styles.restaurantHeader}>
                        <h3 className={styles.restaurantName}>General Recipe Feed</h3>
                        <div className={styles.noMatch}>
                          <span className={styles.matchText}>All recipes shown</span>
                        </div>
                      </div>

                      <div className={styles.menuItems}>
                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={mquinoabowlImage} alt="Mediterranean Quinoa Bowl" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Mediterranean Quinoa Bowl</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              quinoa, chickpeas, olives
                            </p>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={smoothieImage} alt="Green Power Smoothie" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Green Power Smoothie</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              spinach, banana, almond milk
                            </p>
                          </div>
                        </div>

                        <div className={styles.menuItem}>
                          <div className={styles.itemImage}>
                            <img src={porkImage} alt="Pork Tenderloin with Herbs" className={styles.foodImage} />
                          </div>
                          <div className={styles.itemDetails}>
                            <div className={styles.itemHeader}>
                              <h4 className={styles.itemName}>Pork Tenderloin w/ Herbs</h4>
                            </div>
                            <p className={styles.itemDescription}>
                              pork tenderloin, rosemary, garlic
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.comparisonSide}>
                    <div className={styles.comparisonLabel}>With PlatefullAI</div>
                    <div className={styles.platformSlide}>
                      <div className={`${styles.platformHeader} ${styles.recipesHeader}`}>
                        <div className={styles.platformLogo}>Recipe Websites</div>
                      </div>
                      
                      <div className={styles.restaurantHeader}>
                        <h3 className={styles.restaurantName}>Personalized Recipe Feed</h3>
                        <div className={styles.dietaryMatch}>
                          <span className={styles.matchText}>Dietary ID Active</span>
                        </div>
                      </div>

                      <div className={styles.menuItems}>
                        {recipesLoading ? (
                          <div className={styles.loadingState}>
                            <div className={styles.loadingSpinner}></div>
                            <p>Loading personalized recipes...</p>
                          </div>
                        ) : (
                          // Show mock data for demonstration
                          <>
                            <div className={styles.menuItem}>
                              <div className={styles.itemImage}>
                                <img src={mquinoabowlImage} alt="Mediterranean Quinoa Bowl" className={styles.foodImage} />
                              </div>
                              <div className={styles.itemDetails}>
                                <div className={styles.itemHeader}>
                                  <h4 className={styles.itemName}>Mediterranean Quinoa Bowl</h4>
                                  <div className={styles.dietaryTags}>
                                    <span className={`${styles.dietaryTag} ${styles.vegan}`}>Vegan</span>
                                    <span className={`${styles.dietaryTag} ${styles.glutenFree}`}>Gluten-Free</span>
                                  </div>
                                </div>
                                <p className={styles.itemDescription}>
                                  <span className={`${styles.highlightedIngredient} ${styles.like}`}>quinoa</span>, 
                                  <span className={`${styles.highlightedIngredient} ${styles.like}`}>chickpeas</span>, 
                                  <span className={`${styles.highlightedIngredient} ${styles.like}`}>olives</span>
                                </p>
                                <div className={styles.itemPrice}>Perfect for your diet</div>
                              </div>
                            </div>

                            <div className={styles.menuItem}>
                              <div className={styles.itemImage}>
                                <img src={smoothieImage} alt="Green Power Smoothie" className={styles.foodImage} />
                              </div>
                              <div className={styles.itemDetails}>
                                <div className={styles.itemHeader}>
                                  <h4 className={styles.itemName}>Green Power Smoothie</h4>
                                  <div className={styles.dietaryTags}>
                                    <span className={`${styles.dietaryTag} ${styles.vegan}`}>Vegan</span>
                                    <span className={`${styles.dietaryTag} ${styles.dairyFree}`}>Dairy-Free</span>
                                  </div>
                                </div>
                                <p className={styles.itemDescription}>
                                  <span className={`${styles.highlightedIngredient} ${styles.like}`}>spinach</span>, 
                                  <span className={`${styles.highlightedIngredient} ${styles.like}`}>banana</span>, 
                                  <span className={`${styles.highlightedIngredient} ${styles.like}`}>almond milk</span>
                                </p>
                                <div className={styles.itemPrice}>Nutrient-packed breakfast</div>
                              </div>
                            </div>

                            <div className={`${styles.menuItem} ${styles.eliminated}`}>
                              <div className={styles.itemImage}>
                                <img src={porkImage} alt="Pork Tenderloin with Herbs" className={styles.foodImage} />
                              </div>
                              <div className={styles.itemDetails}>
                                <div className={styles.itemHeader}>
                                  <h4 className={styles.itemName}>Pork Tenderloin w/ Herbs</h4>
                                  <div className={styles.dietaryTags}>
                                    <span className={`${styles.dietaryTag} ${styles.eliminated}`}>Contains Pork</span>
                                  </div>
                                </div>
                                <p className={styles.itemDescription}>
                                  <span className={`${styles.highlightedIngredient} ${styles.avoid}`}>pork tenderloin</span>, 
                                  <span className={`${styles.highlightedIngredient} ${styles.avoid}`}>rosemary</span>, 
                                  <span className={`${styles.highlightedIngredient} ${styles.avoid}`}>garlic</span>
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Partner Integrations Section */}
        <div className={styles.partnerIntegrationsSection}>
          <h2 className={styles.sectionTitle}>Partner Integrations</h2>
          <p className={styles.sectionDescription}>
            See how platforms are using PlatefullAI to deliver personalized dining experiences:
          </p>
          
          <div className={styles.integrationCard}>
            <div className={styles.integrationHeader}>
              <h3 className={styles.integrationTitle}>
                <a 
                  href="https://www.usecontxt.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.integrationLink}
                >
                  Contxt × Platefull AI
                </a>
              </h3>
              <div className={styles.integrationBadge}>Live Integration</div>
            </div>
            <p className={styles.integrationDescription}>
              At <a 
                href="https://www.usecontxt.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.integrationTextLink}
              >Contxt</a>, we leverage Platefull AI's powerful dietary intelligence to deliver a truly personalized dining experience. It starts with authentication: our users can sign in via Platefull to securely sync their detailed dietary preferences, allergies, and lifestyle goals. We then use this data to enrich our discovery map, labeling venues with a "viable menu item count" that instantly shows users how many options they'll have at any given restaurant. Finally, the integration extends directly to the table—when a user views a digital menu on Contxt, we automatically highlight compliant dishes and grey out unsuitable ones, ensuring every diner can order with total confidence and ease.
            </p>
            <div className={styles.integrationFooter}>
              <a 
                href="https://www.usecontxt.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.integrationButton}
              >
                Visit Contxt
                <span className={styles.externalLinkIcon}>↗</span>
              </a>
            </div>
          </div>
        </div>

        {/* Business Analytics Section - Only show on portal/landing */}
        {isPortalLanding && (
          <div className={styles.businessAnalyticsSection}>
            <h2 className={styles.sectionTitle}>Track More Than Just Visits</h2>
            <p className={styles.sectionDescription}>
              Get detailed insights into your customers' dietary preferences and health concerns. 
              See exactly what your visitors eat and how to better serve them.
            </p>
            
            <div className={styles.analyticsShowcase}>
              <div className={styles.dashboardCarousel}>
                <div className={styles.carouselContainer}>
                  {dashboards.map((dashboard, index) => (
                    <div 
                      key={dashboard.id} 
                      className={`${styles.carouselSlide} ${activeDashboard === index ? styles.active : ''}`}
                    >
                      <div className={styles.analyticsScreenshot}>
                        <div className={styles.screenshotHeader}>
                          <div className={styles.screenshotTitle}>{dashboard.title}</div>
                          <div className={styles.screenshotSubtitle}>{dashboard.subtitle}</div>
                        </div>
                        <div className={styles.screenshotContent}>
                          <img 
                            src={dashboard.image} 
                            alt={dashboard.alt} 
                            className={styles.dashboardImage}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Navigation arrows */}
                  <button 
                    className={styles.carouselArrow} 
                    onClick={() => goToDashboard(activeDashboard === 0 ? dashboards.length - 1 : activeDashboard - 1)}
                    aria-label="Previous dashboard"
                  >
                    ‹
                  </button>
                  <button 
                    className={`${styles.carouselArrow} ${styles.carouselArrowRight}`} 
                    onClick={() => goToDashboard((activeDashboard + 1) % dashboards.length)}
                    aria-label="Next dashboard"
                  >
                    ›
                  </button>
                </div>
                
                {/* Dot indicators */}
                <div className={styles.carouselIndicators}>
                  {dashboards.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.carouselDot} ${activeDashboard === index ? styles.active : ''}`}
                      onClick={() => goToDashboard(index)}
                      aria-label={`Go to dashboard ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className={styles.analyticsFeatures}>
                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>📊</div>
                  <h3>Real-time Analytics</h3>
                  <p>Track customer dietary preferences and health concerns as they visit your venue</p>
                </div>
                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>👥</div>
                  <h3>Customer Insights</h3>
                  <p>Understand your customer base with detailed diet profiles and preferences</p>
                </div>
                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>📈</div>
                  <h3>Trend Analysis</h3>
                  <p>Identify popular dietary trends and adapt your menu accordingly</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Platform Statistics */}
        <PlatformStats />

        {/* Vision Section */}
        <div className={styles.visionSection}>
          <h2 className={styles.sectionTitle}>The Future of Personalized Food</h2>
          <div className={styles.visionGrid}>
            <div className={styles.visionCard}>
              <div className={styles.visionIcon}></div>
              <h3>Restaurant Integration</h3>
              <p>Walk into any restaurant and have your dietary preferences automatically shared with the kitchen. No more explaining allergies or restrictions.</p>
            </div>
            <div className={styles.visionCard}>
              <div className={styles.visionIcon}></div>
              <h3>Recipe Personalization</h3>
              <p>Every recipe site and cooking app will automatically filter and adapt content to match your exact dietary needs and preferences.</p>
            </div>
            <div className={styles.visionCard}>
              <div className={styles.visionIcon}></div>
              <h3>Smart Shopping</h3>
              <p>Grocery stores and delivery services will know exactly what you can and can't eat, making shopping effortless and safe.</p>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className={styles.progressSection}>
          <h2 className={styles.sectionTitle}>Building the Future of Food</h2>
          <p className={styles.sectionDescription}>
            We're creating a universal dietary identity system that works everywhere you eat:
          </p>
          <div className={styles.progressGrid}>
            <div className={styles.progressCard}>
              <div className={styles.progressIcon}></div>
              <h3>Growing Our Community</h3>
              <p>Building a network of users who want seamless dietary preferences across all food services and restaurants.</p>
            </div>
            <div className={styles.progressCard}>
              <div className={styles.progressIcon}></div>
              <h3>Strategic Partnerships</h3>
              <p>Collaborating with major food platforms, delivery services, and restaurant chains to integrate our system.</p>
            </div>
            <div className={styles.progressCard}>
              <div className={styles.progressIcon}></div>
              <h3>Seamless Integration</h3>
              <p>Making your dietary preferences automatically apply across DoorDash, Uber Eats, grocery stores, and more.</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            {user ? (
              <>
                <h2 className={styles.ctaTitle}>Ready to use your ID?</h2>
                <p className={styles.ctaDescription}>
                  Your digital dietary ID is ready to use! Start by visiting participating restaurants or checking out food websites that integrate with PlatefullAI.
                </p>
                <button 
                  className={styles.primaryButton}
                  onClick={() => window.location.href = '/settings'}
                >
                  Manage Your Preferences
                </button>
              </>
            ) : (
              <>
                <h2 className={styles.ctaTitle}>How to join</h2>
                <p className={styles.ctaDescription}>
                  Sign up, and we'll email you updates as we partner with services.
                </p>
                <div className={styles.ctaButtons}>
                  <button 
                    className={styles.primaryButton}
                    onClick={handleLogin}
                  >
                    Get Started
                  </button>
                  <a 
                    href="/pricing"
                    className={styles.secondaryButton}
                  >
                    View Pricing
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </PageBody>
    </PageContainer>
  )
}

export default Home
